/*
 * Frido Corporate Gifting — enquiry form submit.
 *
 * POSTs the lead to the App Proxy at /apps/frido/orders/corporate-gift, which
 * is a RAW passthrough: it returns Frido's /corporate-gifting-leads status +
 * body verbatim (no { status: "SUCCESS", data } envelope). So this does NOT use
 * window.FridoOrders.proxy() — it treats any 2xx as success and anything else
 * as a failure.
 *
 * Self-contained vanilla JS (theme asset, no build step). Mirrors the IIFE
 * style of frido-orders-core.js / wallet.js.
 */
(function () {
  "use strict";

  // ── Upstream payload contract ──────────────────────────────────────────
  // The proxy is additionalProperties:true and forwards this body verbatim to
  // Frido /corporate-gifting-leads. The exact key names Frido expects are NOT
  // documented on our side — CONFIRM against the Frido API and adjust this one
  // map if a key differs. The DOM input `name`s stay as-is; only the wire keys
  // are remapped here.
  // Confirmed against rapi.myfrido.com/api/v1/corporate-gifting-leads — the
  // upstream validates each field by exact key and rejects unknowns as empty.
  var PAYLOAD_KEYS = {
    full_name: "fullName",
    email: "email",
    phone: "phone",
    company_name: "companyName",
    quantity: "quantity",
  };

  function init(root) {
    var form = root.querySelector("[data-fr-cg-form]");
    if (!form) return;

    var submitBtn = form.querySelector("[data-fr-cg-submit]");
    var submitLabel = form.querySelector("[data-fr-cg-submit-label]");
    var statusEl = form.querySelector("[data-fr-cg-status]");
    var defaultSubmitText = submitLabel ? submitLabel.textContent : "Submit Request";

    var proxyPath = root.getAttribute("data-proxy-path") || "/apps/frido/orders/corporate-gift";
    var successMessage =
      root.getAttribute("data-success-message") ||
      "Thanks! Your enquiry has been submitted.";
    var errorMessage =
      root.getAttribute("data-error-message") ||
      "Something went wrong. Please try again.";

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      if (submitBtn && submitBtn.disabled) return;

      clearStatus();
      var fields = readFields(form);
      var errors = validate(fields);

      if (Object.keys(errors).length) {
        applyErrors(form, errors);
        focusFirstError(form, errors);
        return;
      }

      submit(fields);
    });

    // Clear a field's error state as soon as the user edits it.
    form.addEventListener("input", function (event) {
      var el = event.target;
      if (!el || !el.name) return;
      setFieldError(form, el.name, null);
    });

    function submit(fields) {
      setLoading(true);

      var payload = {};
      Object.keys(PAYLOAD_KEYS).forEach(function (k) {
        payload[PAYLOAD_KEYS[k]] = fields[k];
      });

      var url = new URL(proxyPath, window.location.origin);

      fetch(url.toString(), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          // Passthrough: any 2xx is a successful lead. Read the body defensively
          // (it may be JSON or an HTML gateway/error page) but don't depend on
          // its shape for the success decision.
          return res.text().then(function (text) {
            return { ok: res.ok, status: res.status, text: text };
          });
        })
        .then(function (result) {
          if (result.ok) {
            onSuccess();
          } else {
            onError(messageFromBody(result.text) || errorMessage);
          }
        })
        .catch(function () {
          onError(errorMessage);
        })
        .then(function () {
          setLoading(false);
        });
    }

    function onSuccess() {
      form.reset();
      // Reset the quantity select back to its disabled placeholder.
      var select = form.querySelector(".fr-cg__select");
      if (select) select.selectedIndex = 0;
      showStatus(successMessage, "success");
    }

    function onError(message) {
      showStatus(message, "error");
    }

    function setLoading(loading) {
      if (submitBtn) submitBtn.disabled = loading;
      if (submitLabel) submitLabel.textContent = loading ? "Submitting…" : defaultSubmitText;
    }

    function showStatus(message, kind) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.classList.remove("fr-cg__status--success", "fr-cg__status--error");
      statusEl.classList.add(kind === "success" ? "fr-cg__status--success" : "fr-cg__status--error");
      statusEl.hidden = false;
    }

    function clearStatus() {
      if (!statusEl) return;
      statusEl.hidden = true;
      statusEl.textContent = "";
    }
  }

  // ── Field reading + validation ──────────────────────────────────────────
  function readFields(form) {
    return {
      full_name: value(form, "full_name"),
      email: value(form, "email"),
      phone: digitsOnly(value(form, "phone")),
      company_name: value(form, "company_name"),
      quantity: value(form, "quantity"),
    };
  }

  function validate(fields) {
    var errors = {};
    if (!fields.full_name) errors.full_name = "Please enter your name.";
    if (!fields.email) {
      errors.email = "Please enter your email.";
    } else if (!isEmail(fields.email)) {
      errors.email = "Please enter a valid email.";
    }
    if (!fields.phone) {
      errors.phone = "Please enter your phone number.";
    } else if (fields.phone.length !== 10) {
      errors.phone = "Enter a 10-digit phone number.";
    }
    if (!fields.company_name) errors.company_name = "Please enter your company name.";
    if (!fields.quantity) errors.quantity = "Please choose a quantity.";
    return errors;
  }

  function applyErrors(form, errors) {
    Object.keys(errors).forEach(function (name) {
      setFieldError(form, name, errors[name]);
    });
  }

  function setFieldError(form, name, message) {
    var input = form.querySelector('[name="' + name + '"]');
    var errorEl = form.querySelector('[data-error-for="' + name + '"]');
    if (input) {
      if (name === "phone") {
        var wrap = input.closest(".fr-cg__phone");
        if (wrap) wrap.classList.toggle("fr-cg__phone--invalid", !!message);
      } else {
        input.classList.toggle("fr-cg__input--invalid", !!message);
      }
    }
    if (errorEl) {
      errorEl.textContent = message || "";
      errorEl.hidden = !message;
    }
  }

  function focusFirstError(form, errors) {
    var first = Object.keys(errors)[0];
    if (!first) return;
    var input = form.querySelector('[name="' + first + '"]');
    if (input && typeof input.focus === "function") input.focus();
  }

  // ── Helpers ─────────────────────────────────────────────────────────────
  function value(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? String(el.value || "").trim() : "";
  }

  function digitsOnly(s) {
    return String(s || "").replace(/\D+/g, "");
  }

  function isEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  // Best-effort human message out of a passthrough error body.
  function messageFromBody(text) {
    if (!text) return null;
    try {
      var json = JSON.parse(text);
      // Frido returns { error: "Name cannot be empty" } (string) on validation
      // failures; other shapes use { message } or { error: { message } }.
      if (json && typeof json.error === "string") return json.error;
      if (json && typeof json.message === "string") return json.message;
      if (json && json.error && typeof json.error.message === "string") return json.error.message;
    } catch (e) {
      /* non-JSON body — fall through to the configured error message */
    }
    return null;
  }

  function boot() {
    var roots = document.querySelectorAll("[data-corporate-gifting]");
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
